// React
import type { ReactNode, ElementType, ComponentPropsWithRef } from 'react';

// UI primitives
import { Button } from '@/components/ui/button';

// Icons
import { CloseIcon } from '@/react/components/icons/CloseIcon';

// Hooks and utilities
import { cn } from '@/lib/utils';

interface ModalLayoutProps<T extends ElementType = 'div'> {
  as?: T;
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  closeLabel?: string;
}

/** Default title styling — use inside ModalLayout's title slot for plain text headings. */
export function ModalTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-inter text-lg font-semibold leading-none text-off-black text-wrap-balance">
      {children}
    </h2>
  );
}

export function ModalLayout<T extends ElementType = 'div'>({
  as,
  title,
  onClose,
  footer,
  children,
  className,
  contentClassName,
  scrollRef,
  closeLabel = 'Close',
  ...rest
}: ModalLayoutProps<T> & Omit<ComponentPropsWithRef<T>, keyof ModalLayoutProps<T>>) {
  const Root = as || 'div';

  return (
    <Root className={cn('flex flex-1 flex-col min-h-0', className)} {...rest}>
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-dinnerplate-white px-8 pt-8 pb-3">
        {title}
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          aria-label={closeLabel}
          className="-mr-3 rounded-xs p-3 text-off-black hover:bg-dinnerplate-white-light [&_svg]:size-auto"
        >
          <CloseIcon />
        </Button>
      </div>

      {/* Scrollable content */}
      <div ref={scrollRef} className={cn('flex-1 min-h-0 p-8 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]', contentClassName)}>
        {children}
      </div>

      {/* Footer */}
      {footer != null && (
        <div className="flex shrink-0 items-center border-t border-dinnerplate-white px-8 pt-6 pb-8">
          {footer}
        </div>
      )}
    </Root>
  );
}
